import { defineStore } from "pinia";

import { MerchantApiError } from "../authentication/errors.js";
import {
  DEFAULT_WIDGET_INSTALLATION,
  defaultWidgetPreviewState,
  normalizeWidgetConfig,
  sanitizeWidgetConfig,
  validateWidgetConfig,
  type StorefrontWidgetConfig,
  type MerchantWidgetDiagnosticDto,
  type WidgetInstallation,
  type WidgetPreviewDevice,
  type WidgetPreviewState,
  type WidgetSettingsDto,
} from "./models.js";
import type { WidgetSettingsService } from "./service.js";

export const useWidgetSettingsStore = defineStore("merchant-widget-settings", {
  state: (): {
    config: StorefrontWidgetConfig;
    diagnostics: MerchantWidgetDiagnosticDto[];
    diagnosticsErrorMessage?: string;
    diagnosticsLoading: boolean;
    errorMessage?: string;
    etag?: string;
    installation: WidgetInstallation;
    loading: boolean;
    mutationMessage?: string;
    mutationErrorMessage?: string;
    activationEnabled: boolean;
    activationMutationErrorMessage?: string;
    activationMutationMessage?: string;
    preview: WidgetPreviewState;
    persistedConfig: StorefrontWidgetConfig;
    publicWidgetKey: string;
    saving: boolean;
    savingActivation: boolean;
    service?: WidgetSettingsService;
    validationErrors: string[];
  } => ({
    config: normalizeWidgetConfig(undefined),
    diagnostics: [],
    diagnosticsLoading: false,
    installation: DEFAULT_WIDGET_INSTALLATION,
    loading: false,
    preview: defaultWidgetPreviewState(),
    persistedConfig: normalizeWidgetConfig(undefined),
    activationEnabled: false,
    publicWidgetKey: "",
    saving: false,
    savingActivation: false,
    validationErrors: [],
  }),
  actions: {
    configure(service: WidgetSettingsService): void {
      this.service = service;
    },
    async load(): Promise<void> {
      this.loading = true;
      delete this.errorMessage;
      try {
        this.applyDto(await this.requireService().loadWidget());
      } catch (error: unknown) {
        this.errorMessage = messageForError(error, "Widget settings could not be loaded.");
      } finally {
        this.loading = false;
      }
    },
    async save(): Promise<void> {
      const configToSave = sanitizeWidgetConfig({
        ...this.config,
        enabled: this.persistedConfig.enabled,
      });
      this.validationErrors = validateWidgetConfig(configToSave);
      if (this.validationErrors.length > 0) return;
      if (this.saving || this.savingActivation) return;

      this.saving = true;
      delete this.mutationErrorMessage;
      delete this.mutationMessage;
      try {
        const activationDraft = this.activationEnabled;
        this.applyDto(await this.requireService().saveWidget(configToSave));
        this.activationEnabled = activationDraft;
        this.mutationMessage = "Widget settings saved and audited.";
      } catch (error: unknown) {
        this.mutationErrorMessage = messageForError(error, "Widget settings could not be saved.");
      } finally {
        this.saving = false;
      }
    },
    async saveActivation(): Promise<void> {
      if (this.saving || this.savingActivation) return;
      if (this.activationEnabled === this.persistedConfig.enabled) return;

      this.savingActivation = true;
      delete this.activationMutationErrorMessage;
      delete this.activationMutationMessage;
      try {
        const dto = await this.requireService().saveWidget({
          ...this.persistedConfig,
          enabled: this.activationEnabled,
        });
        const persisted = normalizeWidgetConfig(dto.config);
        this.persistedConfig = persisted;
        this.config.enabled = persisted.enabled;
        this.activationEnabled = persisted.enabled;
        this.activationMutationMessage = "Storefront activation saved.";
      } catch (error: unknown) {
        this.activationMutationErrorMessage = messageForError(
          error,
          "Storefront activation could not be saved.",
        );
      } finally {
        this.savingActivation = false;
      }
    },
    async loadDiagnostics(): Promise<void> {
      this.diagnosticsLoading = true;
      delete this.diagnosticsErrorMessage;
      try {
        this.diagnostics = await this.requireService().loadDiagnostics();
      } catch (error: unknown) {
        this.diagnosticsErrorMessage = messageForError(
          error,
          "Widget diagnostics could not be loaded.",
        );
      } finally {
        this.diagnosticsLoading = false;
      }
    },
    restoreDefaults(): void {
      this.config = {
        ...normalizeWidgetConfig(undefined),
        enabled: this.persistedConfig.enabled,
      };
      this.validationErrors = [];
    },
    setPreviewDevice(device: WidgetPreviewDevice): void {
      this.preview.device = device;
    },
    applyDto(dto: WidgetSettingsDto): void {
      this.persistedConfig = normalizeWidgetConfig(dto.config);
      this.config = normalizeWidgetConfig(this.persistedConfig);
      this.activationEnabled = this.persistedConfig.enabled;
      this.installation = dto.installation;
      this.publicWidgetKey = dto.publicWidgetKey;
      this.etag = dto.etag;
    },
    requireService(): WidgetSettingsService {
      if (this.service === undefined) {
        throw new Error("Widget settings store was not configured before use.");
      }

      return this.service;
    },
  },
});

function messageForError(error: unknown, fallback: string): string {
  if (error instanceof MerchantApiError && error.kind === "access")
    return "Only users with widget settings access can change the widget.";
  if (error instanceof MerchantApiError && error.kind === "validation") return error.message;
  if (error instanceof Error) return error.message;
  return fallback;
}
